import React, { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import AdminDataTable from "../../components/common/AdminDataTable";
import { enquiryColumns } from "../../tables/enquiryColumns";
import EnquiryDetailModal from "./EnquiryDetailModal";
import EnquiryFormModal from "./EnquiryFormModal";
import toast from "../../utils/toast";
import { can } from "../../utils/can";
import * as XLSX from "xlsx";

export default function EnquiryList() {
  const PAGE_SIZE = 10;
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");

  const [enquiries, setEnquiries] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);

  // Modals state
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);

  // Filters (applied)
  const [filters, setFilters] = useState({
    name: "",
    contact: "",
    fromDate: "",
    toDate: "",
  });

  // Temporary inputs
  const [inputFilters, setInputFilters] = useState({
    name: "",
    contact: "",
    fromDate: "",
    toDate: "",
  });

  // Fetch enquiries
  const fetchEnquiries = useCallback(async () => {
    try {
      setListLoading(true);

      const query = new URLSearchParams({
        page,
        pageSize,
        sortBy,
        sortOrder,
        name: filters.name,
        contact: filters.contact,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      });

      const res = await fetch(`${API_URL}/api/admin/enquiry-list?${query.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();

      if (result.success) {
        setEnquiries(result.data || []);
        const total = result.totalCount || (result.data ? result.data.length : 0);
        setTotalCount(total);
        setTotalPages(Math.ceil(total / pageSize) || 1);
      } else {
        setEnquiries([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Fetch Enquiries Error:", err);
      setEnquiries([]);
      setTotalCount(0);
      setTotalPages(1);
      toast.showError("Failed to fetch enquiries");
    } finally {
      setListLoading(false);
    }
  }, [API_URL, token, page, pageSize, sortBy, sortOrder, filters]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  // Handle filter submit
  const handleFilterSubmit = (e) => {
    if (e) e.preventDefault();
    setFilters({ ...inputFilters });
    setPage(1);
    setSelectedIds([]);
  };

  // Handle filter reset
  const handleFilterReset = () => {
    const emptyFilters = { name: "", contact: "", fromDate: "", toDate: "" };
    setInputFilters(emptyFilters);
    setFilters(emptyFilters);
    setPage(1);
    setSelectedIds([]);
  };

  // View details modal
  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowDetailModal(true);
  };

  // Create new enquiry modal
  const handleOpenCreate = () => {
    setEditingEnquiry(null);
    setShowFormModal(true);
  };

  // Edit enquiry modal
  const handleOpenEdit = (enquiry) => {
    setEditingEnquiry(enquiry);
    setShowFormModal(true);
  };

  // Single delete
  const handleDelete = async (id) => {
    if (!id) return;
    if (can("enquiry_list", "delete") === false) {
      toast.showError("You don't have permission to delete enquiries.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this enquiry?")) {
      return;
    }

    try {
      setListLoading(true);
      const res = await fetch(`${API_URL}/api/admin/enquiry-list/delete/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();

      if (result.success) {
        toast.showSuccess(result.message || "Enquiry deleted successfully");
        setSelectedIds((prev) => prev.filter((item) => item !== id));
        if (selectedEnquiry && selectedEnquiry._id === id) {
          setShowDetailModal(false);
          setSelectedEnquiry(null);
        }
        await fetchEnquiries();
      } else {
        toast.showError(result.message || "Failed to delete enquiry");
      }
    } catch (err) {
      console.error("Delete Enquiry Error:", err);
      toast.showError("Error deleting enquiry");
    } finally {
      setListLoading(false);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (can("enquiry_list", "delete") === false) {
      toast.showError("You don't have permission to delete enquiries.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected enquiries?`)) {
      return;
    }

    try {
      setListLoading(true);
      const res = await fetch(`${API_URL}/api/admin/enquiry-list/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selectedIds }),
      });

      const result = await res.json();

      if (result.success) {
        toast.showSuccess(result.message || "Selected enquiries deleted successfully");
        setSelectedIds([]);
        await fetchEnquiries();
      } else {
        toast.showError(result.message || "Failed to delete selected enquiries");
      }
    } catch (err) {
      console.error("Bulk Delete Error:", err);
      toast.showError("Error during bulk delete");
    } finally {
      setListLoading(false);
    }
  };

  // Row selection
  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Select all rows on current page
  const handleSelectAll = () => {
    const pageIds = enquiries.map((item) => item._id).filter(Boolean);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      if (!enquiries || enquiries.length === 0) {
        toast.showError("No data available to export");
        return;
      }

      let exportData = enquiries;
      try {
        const query = new URLSearchParams({
          page: 1,
          pageSize: 2000,
          name: filters.name,
          contact: filters.contact,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
        });

        const res = await fetch(`${API_URL}/api/admin/enquiry-list?${query.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await res.json();
        if (result.success && result.data && result.data.length > 0) {
          exportData = result.data;
        }
      } catch (err) {
        // fallback to current page
      }

      const rows = exportData.map((item, idx) => ({
        "S.No": idx + 1,
        "Date": item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-GB")
          : "-",
        "Time": item.createdAt
          ? new Date(item.createdAt).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          : "-",
        "Name": item.name || "-",
        "Mobile": item.mobile || "-",
        "Email": item.email || "-",
        "Enquiry For": item.enquiry_for || "-",
        "Message": item.message || "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");
      const fileName = `Enquiries_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.showSuccess(`Exported ${rows.length} enquiries to Excel`);
    } catch (error) {
      console.error("Export Error:", error);
      toast.showError("Failed to export Excel file");
    }
  };

  const handleSort = (column) => {
    if (!column) return;
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const pageIds = enquiries.map((item) => item._id).filter(Boolean);
  const isAllCurrentPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const columns = useMemo(
    () =>
      enquiryColumns({
        PAGE_SIZE: pageSize,
        onViewDetails: handleViewDetails,
        onEdit: handleOpenEdit,
        handleDelete: handleDelete,
        onSelectRow: handleSelectRow,
        selectedIds: selectedIds,
        allSelected: isAllCurrentPageSelected,
        onSelectAll: handleSelectAll,
      }),
    [pageSize, selectedIds, isAllCurrentPageSelected, enquiries]
  );

  const isFiltered =
    Boolean(filters.name) ||
    Boolean(filters.contact) ||
    Boolean(filters.fromDate) ||
    Boolean(filters.toDate);

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Customer Support" />

        <div className="right_col" role="main">
          {/* Top Banner & Action Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="p-3 bg-white shadow-sm d-flex flex-wrap justify-content-between align-items-center"
                style={{
                  borderRadius: "8px",
                  borderLeft: "4px solid #bf3272",
                }}
              >
                <div>
                  <h4 className="m-0 font-weight-bold" style={{ color: "#2d3748" }}>
                    <i className="fa fa-envelope mr-2" style={{ color: "#bf3272" }}></i>
                    Enquiry Management
                  </h4>

                </div>

                <div className="d-flex align-items-center flex-wrap mt-2 pt-2 mt-sm-0 gap-2">
                  {/* Total counter badge */}
                  <div
                    className="px-3 py-1 text-center d-flex align-items-center gap-3 rounded mr-2"
                    style={{ backgroundColor: "#fdf2f8", border: "1px solid #fbcfe8" }}
                  >
                    <span className="small text-muted d-block mr-1" style={{ fontSize: "11px", fontWeight: "600" }}>
                      TOTAL ENQUIRIES
                    </span>
                    <strong style={{ fontSize: "17px", color: "#be185d" }}>
                      {totalCount}
                    </strong>
                  </div>

                  {/* Create New Enquiry Button */}
                  <button
                    type="button"
                    onClick={handleOpenCreate}
                    className="btn btn-sm text-white mr-2"
                    style={{
                      backgroundColor: "#bf3272",
                      borderColor: "#bf3272",
                      fontWeight: "600",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="fa fa-plus-circle mr-1"></i> Add Enquiry
                  </button>

                  {/* Export Button */}
                  <button
                    type="button"
                    onClick={handleExportExcel}
                    className="btn btn-sm btn-outline-success mr-2"
                    title="Export to Excel"
                    style={{ fontWeight: "600", display: "inline-flex", alignItems: "center" }}
                  >
                    <i className="fa fa-file-excel-o mr-1"></i> Export Excel
                  </button>

                  {/* Refresh Button */}
                  <button
                    type="button"
                    onClick={fetchEnquiries}
                    className="btn btn-sm btn-outline-secondary"
                    title="Refresh list"
                    disabled={listLoading}
                    style={{ fontWeight: "600", display: "inline-flex", alignItems: "center" }}
                  >
                    <i className={`fa fa-refresh mr-1 ${listLoading ? "fa-spin" : ""}`}></i> Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="x_panel " style={{ borderRadius: "8px" }}>
            <div className="x_title d-flex justify-content-between align-items-center">
              <h2 style={{ fontSize: "16px", fontWeight: "600", color: "#374151" }}>
                Filter & Search
              </h2>
              {/* <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>
              </ul> */}
              <div className="clearfix" />
            </div>

            <div className="x_content ">
              {/* Filter Form */}
              <div
                className="filtercont p-3 mb-3 "
                style={{
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <form onSubmit={handleFilterSubmit}>
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-3 mb-2">
                      <label className="small font-weight-bold text-muted mb-1">
                        <i className="fa fa-user mr-1"></i> Search By Name
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search name..."
                        value={inputFilters.name}
                        onChange={(e) =>
                          setInputFilters({ ...inputFilters, name: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12 col-md-3 mb-2">
                      <label className="small font-weight-bold text-muted mb-1">
                        <i className="fa fa-phone mr-1"></i> Email / Mobile
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search email or phone..."
                        value={inputFilters.contact}
                        onChange={(e) =>
                          setInputFilters({ ...inputFilters, contact: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12 col-md-2 mb-2">
                      <label className="small font-weight-bold text-muted mb-1">
                        <i className="fa fa-calendar mr-1"></i> From Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={inputFilters.fromDate}
                        onChange={(e) =>
                          setInputFilters({ ...inputFilters, fromDate: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12 col-md-2 mb-2">
                      <label className="small font-weight-bold text-muted mb-1">
                        <i className="fa fa-calendar mr-1"></i> To Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={inputFilters.toDate}
                        onChange={(e) =>
                          setInputFilters({ ...inputFilters, toDate: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12 col-md-2 mb-2 d-flex">
                      <button
                        type="submit"
                        className="btn btn-sm text-white flex-grow-1 mr-1"
                        style={{
                          backgroundColor: "#bf3272",
                          borderColor: "#bf3272",
                          fontWeight: "600",
                          borderRadius: "4px",
                        }}
                      >
                        <i className="fa fa-search mr-1"></i> Search
                      </button>

                      {isFiltered && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleFilterReset}
                          title="Reset Filters"
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </div>

              {/* Bulk Action Bar (when rows are selected) */}
              {selectedIds.length > 0 && (
                <div
                  className="p-2 px-3 mb-3 d-flex justify-content-between align-items-center text-white rounded"
                  style={{
                    backgroundColor: "#1f2937",
                    animation: "fadeIn 0.2s ease-in-out",
                  }}
                >
                  <div className="small">
                    <strong>{selectedIds.length}</strong> enquiry
                    {selectedIds.length > 1 ? "ies" : ""} selected
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger py-1 px-3 mr-2"
                      onClick={handleBulkDelete}
                    >
                      <i className="fa fa-trash mr-1"></i> Delete Selected
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light py-1 px-2"
                      onClick={() => setSelectedIds([])}
                    >
                      Deselect
                    </button>
                  </div>
                </div>
              )}

              {/* Data Table */}
              <div className="table-wrapper">
                <AdminDataTable
                  columns={columns}
                  data={enquiries}
                  loading={listLoading}
                  page={page}
                  pageSize={pageSize}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                  onSort={handleSort}
                />
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>

      {/* Enquiry Detail Modal (Read View) */}
      <EnquiryDetailModal
        show={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        enquiry={selectedEnquiry}
        onDelete={handleDelete}
        onEdit={handleOpenEdit}
      />

      {/* Enquiry Form Modal (Create / Edit View) */}
      <EnquiryFormModal
        show={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setEditingEnquiry(null);
        }}
        enquiry={editingEnquiry}
        onSuccess={fetchEnquiries}
      />
    </div>
  );
}
